import { StockOverviewListResponseType } from "@sellar-io/api-v2/types";
import { InfiniteData, UseInfiniteQueryResult } from "@tanstack/react-query";
import {
  Icon,
  ListElement,
  ProfilePicture,
  StockIndicator,
} from "../../../components";
import { MaxOrderQuantityBadge } from "../../../components/badge/MaxOrderQuantityBadge";
import { AvailableFromBadge } from "../../../components/badge/AvailableFromBadge";
import { InfiniteList } from "../../../components/infiniteLoading/InfiniteList";
import { useStockOverviewStore } from "../StockOverviewStore";
import { twMerge } from "tailwind-merge";
import { useEffect, useRef } from "react";

const ProductCard = ({
  productItem,
  onClick,
}: {
  productItem: StockOverviewListResponseType["data"][number];
  onClick: (productItem: StockOverviewListResponseType["data"][number]) => void;
}) => {
  const parentProduct = productItem.Parent;
  const containerTypeDetails = productItem.name?.split("•")[1] ?? null;
  const subTitle =
    parentProduct?.kind && parentProduct.abv && containerTypeDetails
      ? `${parentProduct.kind} // ${parentProduct.abv}% • ${containerTypeDetails}`
      : null;

  const parentProductInactive = !parentProduct?.active;
  const isOutOfStock =
    productItem.availableStock <= 0 && !productItem.canBackorder;
  const isHidden = !productItem.active;

  return (
    <ListElement onClick={() => onClick(productItem)}>
      <div
        className={twMerge(
          "flex w-full items-center justify-between",
          (isOutOfStock || isHidden) && "opacity-50",
        )}
      >
        <div className="flex flex-col gap-2">
          {/* product picture, title and subtitle */}
          <div className="flex items-center gap-2">
            <ProfilePicture
              logoUrl={parentProduct?.heroImageUrl}
              size={"medium"}
            />
            <div>
              {!!productItem.sku && (
                <div className="text-xs">
                  {parentProductInactive && (
                    <span className="text-red-800">
                      Inactive <span className="text-zinc-500"> • </span>
                    </span>
                  )}
                  <span className="text-zinc-500">SKU: {productItem.sku}</span>
                </div>
              )}
              <div className={twMerge("font-medium text-zinc-800")}>
                {parentProduct?.name}
              </div>
              {!!subTitle && (
                <div className={twMerge("text-sm text-zinc-700")}>
                  {subTitle}
                </div>
              )}
            </div>
          </div>
          {/* product item highlight list */}
          <div className="flex flex-wrap items-center gap-1">
            <StockIndicator
              stock={productItem.availableStock}
              canBackorder={productItem.canBackorder}
              type={"short"}
            />
            {!!productItem.maxOrderQuantity && (
              <MaxOrderQuantityBadge
                fullSize={false}
                maxQuantity={productItem.maxOrderQuantity}
              />
            )}
            {!!productItem.availableFrom && (
              <AvailableFromBadge availableFrom={productItem.availableFrom} />
            )}
            {!productItem.active && (
              <Icon icon="EYE_OFF" color="text-black" size={0.9} />
            )}
          </div>
        </div>
      </div>
    </ListElement>
  );
};

export const StockOverviewCardList = ({
  stockOverviewQuery,
  onClickProduct,
}: {
  stockOverviewQuery: UseInfiniteQueryResult<
    InfiniteData<StockOverviewListResponseType>
  >;
  onClickProduct: (productId: number) => void;
}) => {
  const stockOverviewStore = useStockOverviewStore();
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  // scroll to top after changing search/filters
  useEffect(() => {
    scrollContainerRef.current?.scrollTo({ top: 0 });
  }, [
    stockOverviewStore.columnSort,
    stockOverviewStore.search,
    stockOverviewStore.containerType,
    stockOverviewStore.inStock,
    stockOverviewStore.preOrder,
    stockOverviewStore.productStatus,
    stockOverviewStore.itemStatus,
  ]);

  const areFilterOptionsExpanded =
    stockOverviewStore.containerType ||
    stockOverviewStore.inStock ||
    stockOverviewStore.preOrder ||
    stockOverviewStore.productStatus ||
    stockOverviewStore.itemStatus;

  return (
    <div
      className={twMerge(
        "[scrollbar-width:none] overflow-auto border-zinc-300 [::-webkit-scrollbar]:hidden",
        // TODO: perhaps a better solution for dynamically scaling the scrollable box
        areFilterOptionsExpanded
          ? "max-h-[calc(100vh-300px)]"
          : "max-h-[calc(100vh-220px)]",
      )}
      ref={scrollContainerRef}
    >
      <InfiniteList query={stockOverviewQuery}>
        {stockOverviewQuery.data?.pages.map((page) =>
          page.data.map((productItem) => (
            <ProductCard
              productItem={productItem}
              key={productItem.id}
              onClick={(productItem) => onClickProduct(productItem.id)}
            />
          )),
        )}
      </InfiniteList>
    </div>
  );
};
